import type { ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Spin, Row, Col, Typography, Space, Empty, Descriptions, Tag, Collapse } from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  HomeOutlined,
  MailOutlined,
  PhoneOutlined,
  FileTextOutlined,
  ShoppingOutlined,
  LockOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useWisecureQuery } from '@wisecure/api-client/hooks';
import type {
  AdminSecurityEstimateResponse,
  AdminSecurityRequestDetailResponse,
} from '@wisecure/api-client';
import { queryKeys } from '@/api/query-keys';
import { CopyIdButton } from '@/components/CopyIdButton';
import { WisecureCard } from '@/components/base';
import { neutral, primary, error } from '@/theme';
import { clientDisplayName, formatDateTime, formatEuro, REQUEST_TYPE_LABELS } from '@/components/security/labels';
import { CategoryTags } from '@/components/security/RequestTags';
import {
  SecurityRequestStatusSwitch,
  SecurityRequestStatusTag,
} from '@/components/security/SecurityRequestStatus';
import { EstimateEquipment } from '@/components/security/EstimateEquipment';
import { InternalMarginBreakdown } from '@/components/security/InternalMarginBreakdown';
import { AdminNoteCard } from '@/components/security/AdminNoteCard';

const { Text, Title } = Typography;

const cardTitle = (icon: ReactNode, label: string) => (
  <Space>
    {icon}
    <span>{label}</span>
  </Space>
);

/** The stored estimate is untyped JSON on the API: only trust it when it has the expected shape. */
function asEstimate(raw: AdminSecurityRequestDetailResponse['estimate']): AdminSecurityEstimateResponse | null {
  if (!raw || typeof raw.totalPrice !== 'number') return null;
  return raw as AdminSecurityEstimateResponse;
}

export function SecurityRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: request, isLoading } = useWisecureQuery<AdminSecurityRequestDetailResponse>(
    queryKeys.securityRequestDetail(id ?? ''),
    (client) => client.dashboardSecurity.securityDashboardControllerFindOne({ id: id! }),
    { enabled: !!id },
  );

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!request) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Empty description="Demande non trouvée">
          <Button onClick={() => navigate('/security-requests')}>Retour à la liste</Button>
        </Empty>
      </div>
    );
  }

  const estimate = asEstimate(request.estimate);
  const { client, location } = request;

  return (
    <div style={{ maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/security-requests')}
          style={{ color: neutral[500] }}
        />
        <Text style={{ color: neutral[300], fontSize: 13 }}>Demandes sécurité</Text>
        <Text style={{ color: neutral[200] }}>/</Text>
        <Text style={{ color: neutral[700], fontWeight: 500, fontSize: 13 }}>{request.id.slice(0, 8)}...</Text>
      </div>

      <WisecureCard style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <Space direction="vertical" size={8}>
            <Space size={8} wrap>
              <Title level={4} style={{ margin: 0 }}>
                {REQUEST_TYPE_LABELS[request.type]} · {clientDisplayName(client)}
              </Title>
              <SecurityRequestStatusTag status={request.status} />
            </Space>
            <Space size={8} wrap>
              <CategoryTags categories={request.categories} />
              {request.summary && <Text style={{ color: neutral[500] }}>{request.summary}</Text>}
            </Space>
            <Text style={{ color: neutral[500], fontSize: 13 }}>
              Reçue le {formatDateTime(request.createdAt)}
              {request.estimatedPrice != null && <> · Prix estimé {formatEuro(request.estimatedPrice)}</>}
            </Text>
          </Space>
          <Space direction="vertical" align="end" size={8}>
            <SecurityRequestStatusSwitch id={request.id} status={request.status} />
            {request.handledAt && (
              <Text style={{ color: neutral[500], fontSize: 12 }}>
                <CheckCircleOutlined style={{ marginRight: 4 }} />
                Traitée le {formatDateTime(request.handledAt)}
                {request.handledBy ? ` par ${request.handledBy}` : ''}
              </Text>
            )}
            <CopyIdButton id={request.id} />
          </Space>
        </div>
      </WisecureCard>

      <Row gutter={24}>
        <Col xs={24} lg={14}>
          <WisecureCard
            title={cardTitle(<FileTextOutlined style={{ color: primary[400] }} />, 'Demande')}
            style={{ marginBottom: 24 }}
          >
            {request.detailLines.length ? (
              <Descriptions column={1} size="small" bordered styles={{ label: { width: 200 } }}>
                {request.detailLines.map((line, index) => (
                  <Descriptions.Item key={`${line.label}-${index}`} label={line.label}>
                    <span style={{ whiteSpace: 'pre-wrap' }}>{line.value}</span>
                  </Descriptions.Item>
                ))}
              </Descriptions>
            ) : (
              <Empty description="Aucun détail" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </WisecureCard>
        </Col>

        <Col xs={24} lg={10}>
          <WisecureCard
            title={cardTitle(<UserOutlined style={{ color: primary[400] }} />, 'Client')}
            style={{ marginBottom: 24 }}
            extra={
              <Button size="small" onClick={() => navigate(`/users/${client.userId}`)}>
                Voir le profil
              </Button>
            }
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Nom">{clientDisplayName(client)}</Descriptions.Item>
              <Descriptions.Item label={<><MailOutlined style={{ marginRight: 6 }} />E-mail</>}>
                <a href={`mailto:${client.email}`}>{client.email}</a>
              </Descriptions.Item>
              <Descriptions.Item label={<><PhoneOutlined style={{ marginRight: 6 }} />Téléphone</>}>
                {client.phone ? <a href={`tel:${client.phone}`}>{client.phone}</a> : '—'}
              </Descriptions.Item>
            </Descriptions>
          </WisecureCard>

          <WisecureCard
            title={cardTitle(<HomeOutlined style={{ color: primary[400] }} />, 'Lieu')}
            style={{ marginBottom: 24 }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Libellé">{location.label}</Descriptions.Item>
              <Descriptions.Item label="Adresse">{location.address}</Descriptions.Item>
              {!location.id && (
                <Descriptions.Item label="État">
                  <Tag>Lieu supprimé depuis</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
          </WisecureCard>

          <div style={{ marginBottom: 24 }}>
            <AdminNoteCard key={request.adminNote ?? ''} id={request.id} note={request.adminNote} />
          </div>
        </Col>
      </Row>

      {estimate && (
        <>
          <WisecureCard
            title={cardTitle(<ShoppingOutlined style={{ color: primary[400] }} />, 'Estimation et matériel')}
            style={{ marginBottom: 24 }}
          >
            <EstimateEquipment estimate={estimate} />
          </WisecureCard>

          {estimate.internal && (
            <WisecureCard
              title={cardTitle(<LockOutlined style={{ color: error[400] }} />, 'Interne — marge Wisecure')}
              extra={<Tag color="red">Ne jamais communiquer au client</Tag>}
              style={{ marginBottom: 24, borderColor: error[100] }}
            >
              <InternalMarginBreakdown internal={estimate.internal} />
            </WisecureCard>
          )}
        </>
      )}

      {(request.configuration || request.details) && (
        <Collapse
          style={{ marginBottom: 24 }}
          items={[
            {
              key: 'raw',
              label: 'Données brutes (configurateur)',
              children: (
                <pre style={{ fontSize: 12, margin: 0, whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify({ configuration: request.configuration, details: request.details }, null, 2)}
                </pre>
              ),
            },
          ]}
        />
      )}
    </div>
  );
}
